import { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Menu, Radio, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PUBLIC_ROUTES, ROUTES } from '@/constants';
import config from '@/config';
import { Button } from '@/components/ui/button';
import { ButtonLink } from '@/components/ui/button-link';
import useIsMobile from '@/hooks/useIsMobile';
import PublicSearchDialog from './PublicSearchDialog';

const NAV_LINKS = [
  { label: 'Home', to: PUBLIC_ROUTES.HOME },
  { label: 'Tournaments', to: PUBLIC_ROUTES.TOURNAMENTS },
  { label: 'Fixtures', to: PUBLIC_ROUTES.FIXTURES },
  { label: 'Results', to: PUBLIC_ROUTES.RESULTS },
  { label: 'Live', to: PUBLIC_ROUTES.LIVE, highlight: true },
  { label: 'Teams', to: PUBLIC_ROUTES.TEAMS },
  { label: 'Players', to: PUBLIC_ROUTES.PLAYERS },
  { label: 'Grounds', to: PUBLIC_ROUTES.GROUNDS },
  { label: 'Gallery', to: PUBLIC_ROUTES.GALLERY },
];

function NavItem({ link, onNavigate }) {
  return (
    <NavLink
      to={link.to}
      end={link.to === PUBLIC_ROUTES.HOME}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'relative rounded-lg px-3 py-2 text-sm font-medium transition-colors',
          isActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <motion.span
              layoutId="public-nav-active"
              className="vp-nav-active-indicator"
              transition={{ type: 'spring', stiffness: 380, damping: 30 }}
              aria-hidden="true"
            />
          )}
          <span className="relative z-10 inline-flex items-center gap-1">
            {link.highlight ? (
              <>
                <Radio className={cn('h-3.5 w-3.5', isActive ? 'text-live' : 'text-live/70')} aria-hidden="true" />
                {link.label}
              </>
            ) : (
              link.label
            )}
          </span>
        </>
      )}
    </NavLink>
  );
}

function PublicNav({ className }) {
  const isMobile = useIsMobile();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  return (
    <>
      <header
        className={cn(
          'sticky top-0 z-50 border-b border-border/60 bg-background/75 shadow-sm backdrop-blur-xl supports-[backdrop-filter]:bg-background/60',
          className
        )}
      >
        <div className="vp-container-wide flex h-16 items-center justify-between gap-4">
          <Link
            to={PUBLIC_ROUTES.HOME}
            className="group flex shrink-0 items-center gap-2 font-display text-lg font-extrabold tracking-tight"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-accent-cyan text-sm font-black text-white shadow-md transition-transform group-hover:scale-105">
              VP
            </span>
            <span className="hidden sm:inline">{config.appName}</span>
          </Link>

          {!isMobile && (
            <nav aria-label="Main navigation" className="hidden flex-1 items-center justify-center xl:flex">
              <ul className="flex flex-wrap items-center gap-0.5">
                {NAV_LINKS.map((link) => (
                  <li key={link.to}>
                    <NavItem link={link} />
                  </li>
                ))}
              </ul>
            </nav>
          )}

          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="rounded-full"
              aria-label="Open search"
              onClick={() => setSearchOpen(true)}
            >
              <Search className="h-5 w-5" />
            </Button>

            <ButtonLink to={PUBLIC_ROUTES.LIVE} size="sm" className="hidden sm:inline-flex">
              <Radio className="mr-1.5 h-4 w-4" aria-hidden="true" />
              Live
            </ButtonLink>

            <ButtonLink to={ROUTES.LOGIN} variant="outline" size="sm" className="hidden md:inline-flex">
              Sign In
            </ButtonLink>

            {isMobile && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((open) => !open)}
              >
                {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            )}
          </div>
        </div>

        <AnimatePresence>
          {isMobile && menuOpen && (
            <motion.nav
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              aria-label="Mobile navigation"
              className="overflow-hidden border-t border-border bg-card/95 backdrop-blur-xl xl:hidden"
            >
              <ul className="space-y-1 px-4 py-4">
                {NAV_LINKS.map((link) => (
                  <li key={link.to}>
                    <NavItem link={link} onNavigate={() => setMenuOpen(false)} />
                  </li>
                ))}
                <li className="pt-2">
                  <ButtonLink to={ROUTES.LOGIN} variant="secondary" className="w-full" onClick={() => setMenuOpen(false)}>
                    Sign In
                  </ButtonLink>
                </li>
              </ul>
            </motion.nav>
          )}
        </AnimatePresence>
      </header>

      <PublicSearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
    </>
  );
}

export default PublicNav;
